import './AmcPathingAnalysis.scss';
import { endOfMonth, format, parseISO, startOfMonth, subMonths } from 'date-fns';
import { ReactNode, useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSetError } from '@src/redux/modals';
import { HorizontalBarChart } from '@Components/Charts/HorizontalBarChart';
import { ChartContainer } from '@Components/ChartContainer';
import { BPMTable, Header } from '@Components/StickyTable/BPMTable';
import { CellRenderer } from '@Components/StickyTable/StickyTable';
import { Design, Placement } from '@Components/designConstants';
import { DatePicker } from '@Components/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { AmcAdPathFilterMenu } from './AmcAdPathFilters';
import { AmcTouchpointPieChart, AmcTouchpointSlice } from './AmcTouchpointPieChart';
import {
  AD_TYPE_CHART_COLORS,
  adTypePathTableHeaders,
  campaignAttrTableHeaders,
  campaignAttrTableTooltips,
  campaignPathTableHeaders,
  DEFAULT_AD_TYPE_FILTERS,
  HEADER_HEIGHT
} from './amc-pathing-analysis-config';
import {
  calculateTotalPurchaseData,
  filterAdTypes,
  filterCampaignTypes,
  filterFirstTouchCampaignData,
  filterTouchpointData,
  formatCampaignAndAdTypeData,
  groupAttributionData
} from './amc-pathing-analysis-utils';
import {
  AdTypeFilterGroup,
  AdTypeFilterOption,
  AmcPathingTouchpointRow
} from './amc-pathing-analysis-types';
import { useAmcPathingAnalysisData } from './utils/useAmcPathingAnalysisData';

const DATE_FORMAT = 'yyyy-MM-dd';
const BAR_FILL_COLOR = '#0874f4';
const BAR_CHART_HEIGHT = 500;

// AMC's pathing report for the previous month isn't ready until the 4th of the current month.
const monthsBack = new Date().getDate() < 4 ? 2 : 1;
const DEFAULT_DATE_RANGE: DateRange = {
  start: format(startOfMonth(subMonths(new Date(), monthsBack)), DATE_FORMAT),
  end: format(startOfMonth(subMonths(new Date(), monthsBack)), DATE_FORMAT)
};

const NoPathingData = () => <div className='amc-pathing-no-data'>No data to show</div>;

const toSlices = (
  rows: AmcPathingTouchpointRow[],
  conversionsKey: 'totalPurchases' | 'newToBrandTotalPurchases'
): AmcTouchpointSlice[] =>
  rows.map(row => {
    const colors = AD_TYPE_CHART_COLORS[row.adType.toLocaleLowerCase()] ?? AD_TYPE_CHART_COLORS[''];
    return {
      adType: row.adType,
      label: colors.label,
      value: row[conversionsKey],
      fillColor: colors.fillColor
    };
  });

const AdTypeLegend = () => (
  <div className='ad-type-legend'>
    {Object.values(AD_TYPE_CHART_COLORS).map(({ label, fillColor }) => (
      <div className='ad-type-legend-key' key={label}>
        <div className='ad-type-legend-color' style={{ backgroundColor: fillColor }} />
        {label}
      </div>
    ))}
  </div>
);

interface TouchpointQuestion {
  header: ReactNode;
  slices: AmcTouchpointSlice[];
}

const TOUCHPOINT_CHART_MIN_HEIGHT = 200;

const TouchpointRow = ({
  questions,
  isLoading
}: {
  questions: TouchpointQuestion[];
  isLoading: boolean;
}) => (
  <div className='amc-pathing-touchpoint-row'>
    {questions.map(({ header, slices }) => (
      <ChartContainer
        key={String(header)}
        title={header}
        isLoading={isLoading}
        minHeight={TOUCHPOINT_CHART_MIN_HEIGHT}
      >
        <AmcTouchpointPieChart data={slices} />
      </ChartContainer>
    ))}
  </div>
);

const attrHeadersRenderer: CellRenderer<Header> = ({ data }) => (
  <OverlayTrigger
    placement={Placement.TOP_END}
    overlay={<Tooltip id={`tooltip-${data}`}>{campaignAttrTableTooltips[String(data)]}</Tooltip>}
  >
    <span>{String(data)}</span>
  </OverlayTrigger>
);

interface AmcPathingAnalysisProps {
  amcInstanceId?: string;
}

export const AmcPathingAnalysis = ({ amcInstanceId }: AmcPathingAnalysisProps) => {
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [adTypeFilters, setAdTypeFilters] = useState<AdTypeFilterGroup[]>(DEFAULT_AD_TYPE_FILTERS);
  const setError = useSetError();

  const {
    data: pathingData,
    isLoading,
    isFetching,
    error
  } = useAmcPathingAnalysisData(
    amcInstanceId,
    dateRange.start,
    format(endOfMonth(parseISO(dateRange.end)), DATE_FORMAT)
  );

  useEffect(() => {
    if (error) {
      setError({
        title: 'Error fetching Pathing Analysis data',
        message: 'There was an error fetching the Pathing Analysis data. Please try again later.'
      });
    }
  }, [error, setError]);

  const isBusy = isFetching || isLoading;

  const selectAdTypeFilter = (
    value: AdTypeFilterOption['value'],
    rowIndex: number,
    filterIndex: number
  ) => {
    setAdTypeFilters(current =>
      current.map((row, i) =>
        i !== rowIndex
          ? row
          : {
              ...row,
              data: row.data.map((filter, j) => (j !== filterIndex ? filter : { ...filter, value }))
            }
      )
    );
  };

  const touchpointData = pathingData?.adTypeTouchpointData ?? [];
  const firstTouchpointData = filterTouchpointData(touchpointData, 'first_touchpoint');
  const lastTouchpointData = filterTouchpointData(touchpointData, 'last_touchpoint');
  // bpm-frontend's original headers didn't match the data they labeled (e.g. "NTB conversions
  // when served first" actually pulled last-touch data) — replaced with short, accurate labels
  // for the real axes here: touchpoint (first/last) x conversion scope (all/NTB).
  const touchpointQuestions: TouchpointQuestion[] = [
    {
      header: 'First Touch — All Conversions',
      slices: toSlices(firstTouchpointData, 'totalPurchases')
    },
    {
      header: 'Last Touch — All Conversions',
      slices: toSlices(lastTouchpointData, 'totalPurchases')
    },
    {
      header: 'First Touch — NTB Conversions',
      slices: toSlices(firstTouchpointData, 'newToBrandTotalPurchases')
    },
    {
      header: 'Last Touch — NTB Conversions',
      slices: toSlices(lastTouchpointData, 'newToBrandTotalPurchases')
    }
  ];

  const pathData = pathingData?.campaignAndAdTypePathData ?? [];
  const campaignPathData = formatCampaignAndAdTypeData(pathData);
  const adTypePathData = filterAdTypes(pathData, adTypeFilters);
  const adTypeTotalPurchaseData = calculateTotalPurchaseData(touchpointData);
  const campaignOccurrenceData = filterCampaignTypes(
    pathingData?.campaignAndAdTypeOccurrenceData ?? []
  );
  const attributionData = pathingData?.firstLastLinearAndPositionCombinedData ?? [];
  const campaignAttrData = groupAttributionData(attributionData);
  const firstTouchCampaignData = filterFirstTouchCampaignData(attributionData);

  return (
    <div className='amc-pathing-analysis'>
      <div className='amc-pathing-analysis-header'>
        <div className='amc-pathing-analysis-disclaimer'>
          Note: Attribution window used is 28 days.
        </div>
        <DatePicker
          range={dateRange}
          onChange={setDateRange}
          fullMonthsOnly
          fiscalCalOverride={{}}
          design={Design.NEUTRAL}
        />
      </div>

      <AdTypeLegend />
      <TouchpointRow questions={touchpointQuestions} isLoading={isBusy} />

      <ChartContainer title='Top Campaign Paths' isLoading={isBusy}>
        <div className='amc-pathing-table-wrapper'>
          <BPMTable
            autoHeight
            pagination
            data={campaignPathData}
            filterBar={false}
            headers={campaignPathTableHeaders}
            noRowsRenderer={NoPathingData}
            headerHeight={HEADER_HEIGHT}
          />
        </div>
      </ChartContainer>

      <ChartContainer title='Conversions by Ad Type' isLoading={isBusy}>
        <HorizontalBarChart
          data={adTypeTotalPurchaseData}
          barKey='totalPurchases'
          barFillColor={BAR_FILL_COLOR}
          yAxisKey='adType'
          yAxisLabel='Total Purchases'
          height={400}
        />
      </ChartContainer>

      <ChartContainer title='Top Campaigns by Path Occurrence' isLoading={isBusy}>
        <HorizontalBarChart
          data={campaignOccurrenceData}
          barKey='userThatPurchased'
          barFillColor={BAR_FILL_COLOR}
          yAxisKey='campaignAndAdType'
          yAxisLabel='Total Purchasers'
          height={BAR_CHART_HEIGHT}
        />
      </ChartContainer>

      <ChartContainer title='Top First-Touch Campaigns' isLoading={isBusy}>
        <HorizontalBarChart
          data={firstTouchCampaignData}
          barKey='usersThatConverted'
          barFillColor={BAR_FILL_COLOR}
          yAxisKey='campaign'
          yAxisLabel='First Touch Purchasers'
          height={BAR_CHART_HEIGHT}
        />
      </ChartContainer>

      <ChartContainer title='Campaign Performance by Attribution Model' isLoading={isBusy}>
        <div className='amc-pathing-table-wrapper'>
          <BPMTable
            autoHeight
            pagination
            data={campaignAttrData}
            filterBar={false}
            headers={campaignAttrTableHeaders}
            headersRenderer={attrHeadersRenderer}
            noRowsRenderer={NoPathingData}
            headerHeight={HEADER_HEIGHT}
          />
        </div>
      </ChartContainer>

      <ChartContainer title='Purchase Rate by Ad Type Path' isLoading={isBusy}>
        <div className='amc-pathing-ad-path-section'>
          <AmcAdPathFilterMenu filtersRowData={adTypeFilters} onSelect={selectAdTypeFilter} />
          <div className='amc-pathing-table-wrapper'>
            <BPMTable
              autoHeight
              pagination
              data={adTypePathData}
              filterBar={false}
              headers={adTypePathTableHeaders}
              noRowsRenderer={NoPathingData}
              headerHeight={HEADER_HEIGHT}
            />
          </div>
        </div>
      </ChartContainer>
    </div>
  );
};
